import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Save, RotateCcw, GripVertical, ExternalLink, Rows3 } from "lucide-react";
import {
  DndContext, closestCorners, PointerSensor, TouchSensor, KeyboardSensor,
  useSensor, useSensors, useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const ROWS = [
  { id: "primary", label: "Primary Row", hint: "Top nav — the main things people DO with Coach." },
  { id: "secondary", label: "Secondary Row", hint: "Lower nav — About / content links." },
];

function badgePill(badge) {
  if (badge === "new") return <span className="text-[8px] tracking-[0.2em] bg-[#FF331F] text-white px-1 py-0.5 rounded-sm">NEW</span>;
  if (badge === "new_or_soon") return <span className="text-[8px] tracking-[0.2em] bg-[#FF331F] text-white px-1 py-0.5 rounded-sm">NEW/SOON</span>;
  return null;
}

function SortableItem({ item, onLabelChange }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 border border-[#27272A] bg-[#0F0F0F] px-3 py-3 rounded-sm select-none"
      data-testid={`nav-order-item-${item.id}`}
    >
      <button
        type="button"
        className="touch-none cursor-grab active:cursor-grabbing text-zinc-500 hover:text-white flex-shrink-0"
        {...attributes}
        {...listeners}
        aria-label={`Drag ${item.label}`}
        data-testid={`nav-drag-${item.id}`}
      >
        <GripVertical className="h-5 w-5" />
      </button>
      <Input
        value={item.label}
        onChange={(e) => onLabelChange(item.id, e.target.value)}
        className="h-9 bg-[#050505] border-[#27272A] focus:border-[#FF331F] focus:ring-[#FF331F] rounded-sm text-sm font-display uppercase tracking-wide text-white"
        data-testid={`nav-label-${item.id}`}
        aria-label={`Menu label for ${item.id}`}
      />
      {item.external && <ExternalLink className="h-3 w-3 text-zinc-500 flex-shrink-0" title="External link" />}
      {badgePill(item.badge)}
    </div>
  );
}

function Column({ rowId, label, hint, itemIds, meta, onLabelChange }) {
  const { setNodeRef, isOver } = useDroppable({ id: rowId });
  return (
    <div className="flex-1 min-w-0">
      <div className="mb-2">
        <div className="text-white font-display uppercase tracking-widest text-sm flex items-center gap-2"><Rows3 className="h-4 w-4 text-[#FF331F]" /> {label}</div>
        <p className="text-[11px] text-zinc-500">{hint}</p>
      </div>
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <div
          ref={setNodeRef}
          data-testid={`nav-column-${rowId}`}
          className={`space-y-2 rounded-sm border-2 border-dashed p-2 min-h-[120px] transition-colors ${isOver ? "border-[#FF331F] bg-[#FF331F]/5" : "border-[#27272A]"}`}
        >
          {itemIds.length === 0 && <div className="text-center text-[11px] text-zinc-600 py-8">Drop items here</div>}
          {itemIds.map((id) => (
            <SortableItem key={id} item={meta[id]} onLabelChange={onLabelChange} />
          ))}
        </div>
      </SortableContext>
    </div>
  );
}

export default function NavOrder() {
  const [cols, setCols] = useState({ primary: [], secondary: [] });
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const hydrate = (items) => {
    const m = {};
    const c = { primary: [], secondary: [] };
    for (const it of items) {
      m[it.id] = it;
      (c[it.row] || c.primary).push(it.id);
    }
    setMeta(m);
    setCols(c);
  };

  const load = async () => {
    try {
      const { data } = await api.get("/admin/nav");
      hydrate(data?.items || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const findContainer = (id) => {
    if (id in cols) return id;
    return Object.keys(cols).find((k) => cols[k].includes(id));
  };

  const onLabelChange = (id, value) => {
    setMeta((m) => ({ ...m, [id]: { ...m[id], label: value } }));
    setDirty(true);
  };

  const onDragOver = ({ active, over }) => {
    if (!over) return;
    const from = findContainer(active.id);
    const to = findContainer(over.id);
    if (!from || !to || from === to) return;
    setCols((prev) => {
      const fromItems = [...prev[from]];
      const toItems = [...prev[to]];
      const activeIdx = fromItems.indexOf(active.id);
      fromItems.splice(activeIdx, 1);
      const overIdx = toItems.indexOf(over.id);
      const insertAt = overIdx >= 0 ? overIdx : toItems.length;
      toItems.splice(insertAt, 0, active.id);
      return { ...prev, [from]: fromItems, [to]: toItems };
    });
    setDirty(true);
  };

  const onDragEnd = ({ active, over }) => {
    if (!over) return;
    const from = findContainer(active.id);
    const to = findContainer(over.id);
    if (!from || !to) return;
    if (from === to) {
      const items = cols[from];
      const oldIdx = items.indexOf(active.id);
      const newIdx = items.indexOf(over.id);
      if (oldIdx !== newIdx && newIdx >= 0) {
        setCols((prev) => ({ ...prev, [from]: arrayMove(prev[from], oldIdx, newIdx) }));
        setDirty(true);
      }
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const items = [];
      ["primary", "secondary"].forEach((row) => {
        cols[row].forEach((id, idx) => items.push({ id, row, order: idx, label: meta[id]?.label }));
      });
      const { data } = await api.put("/admin/nav", { items });
      hydrate(data?.items || []);
      setDirty(false);
      toast.success("Navigation order saved. Publish to push it live.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset the navigation to its default order?")) return;
    try {
      const { data } = await api.post("/admin/nav/reset");
      hydrate(data?.items || []);
      setDirty(false);
      toast.success("Reverted to default order.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  return (
    <div className="p-8 md:p-12" data-testid="nav-order-admin">
      <div className="mb-8 max-w-3xl">
        <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-2">Site Control</div>
        <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight">Navigation Order.</h1>
        <p className="text-zinc-400 text-sm mt-2">
          Rename any menu item by typing in its box, drag the grip handle to reorder, and drag items between the two rows to move them up to the Primary row or down to the Secondary row. Changes go live after you Save and Publish.
        </p>
      </div>

      <div className="flex items-center gap-2 mb-6">
        <Button onClick={save} disabled={!dirty || saving} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest text-xs rounded-sm" data-testid="nav-order-save">
          <Save className="h-3 w-3 mr-1" /> {saving ? "Saving…" : "Save order"}
        </Button>
        <Button onClick={reset} variant="outline" className="border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-900 bg-transparent font-bold uppercase tracking-widest text-xs rounded-sm" data-testid="nav-order-reset">
          <RotateCcw className="h-3 w-3 mr-1" /> Reset to default
        </Button>
        {dirty && <span className="text-[11px] text-amber-400 uppercase tracking-widest">Unsaved changes</span>}
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">Loading…</div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCorners} onDragOver={onDragOver} onDragEnd={onDragEnd}>
          <div className="flex flex-col lg:flex-row gap-6 max-w-5xl">
            {ROWS.map((r) => (
              <Column key={r.id} rowId={r.id} label={r.label} hint={r.hint} itemIds={cols[r.id]} meta={meta} onLabelChange={onLabelChange} />
            ))}
          </div>
        </DndContext>
      )}
    </div>
  );
}
